import { queryOptions } from '@tanstack/react-query';
import type { Event } from '../data/event';
import type { ExceptionLog } from '../data/exception-log';
import type { OnboardingOverview } from '../data/onboarding';
import type { Order } from '../data/order';
import type { Product } from '../data/product';
import type { Receipt } from '../data/receipt';
import type { ReceivingSession, VendorInstructions } from '../data/receiving';
import type { Store } from '../data/store';
import type { Upload } from '../data/upload';
import type { UploaderConnection } from '../data/uploader-connection';
import type { Vendor, VendorIntegration } from '../data/vendor';
import { persistMeta } from './persisted-schemas';

const SECONDS = 1000;
const MINUTES = 60 * SECONDS;
const HOURS = 60 * MINUTES;

export const storesQueryOptions = () =>
  queryOptions<Store[]>({
    queryKey: ['stores'],
    meta: persistMeta('stores'),
    staleTime: 12 * HOURS,
    gcTime: 24 * HOURS,
    retry: 2,
  });

export const productsQueryOptions = (storeId: string) =>
  queryOptions<Product[]>({
    queryKey: ['stores', storeId, 'products'],
    meta: persistMeta('products'),
    // Products change only on nightly uploads; route loaders + refetch-on-focus
    // cover freshness, so no background poll is needed.
    staleTime: 30 * MINUTES,
    gcTime: 24 * HOURS,
    retry: 2,
  });

export const vendorsQueryOptions = (storeId: string) =>
  queryOptions<Vendor[]>({
    queryKey: ['stores', storeId, 'vendors'],
    meta: persistMeta('vendors'),
    staleTime: 12 * HOURS,
    gcTime: 24 * HOURS,
    retry: 2,
  });

export const vendorIntegrationsQueryOptions = (storeId: string) =>
  queryOptions<VendorIntegration[]>({
    queryKey: ['stores', storeId, 'vendor-integrations'],
    staleTime: 24 * HOURS,
    gcTime: 24 * HOURS,
    retry: 2,
  });

export const ordersQueryOptions = (storeId: string) =>
  queryOptions<Order[]>({
    queryKey: ['stores', storeId, 'orders'],
    meta: persistMeta('orders'),
    staleTime: 3 * HOURS,
    gcTime: 12 * HOURS,
    retry: 2,
  });

export const uploadsQueryOptions = (storeId: string) =>
  queryOptions<Upload[]>({
    queryKey: ['stores', storeId, 'uploads'],
    meta: persistMeta('uploads'),
    // Active monitoring is the gated 5s poll in useUploads; this only governs
    // refetch-on-focus, so a short window is unnecessary churn.
    staleTime: 2 * MINUTES,
    gcTime: 1 * HOURS,
    retry: 2,
  });

export const companionConnectionsQueryOptions = (storeId: string) =>
  queryOptions<UploaderConnection[]>({
    queryKey: ['stores', storeId, 'companions', 'connections'],
    // WebSocket (companion_status_changed) delivers real-time updates, so the
    // focus-refetch window can be generous.
    staleTime: 2 * MINUTES,
    gcTime: 1 * HOURS,
    retry: 2,
  });

export const exceptionLogsQueryOptions = (storeId: string) =>
  queryOptions<ExceptionLog[]>({
    queryKey: ['stores', storeId, 'exception-logs'],
    meta: persistMeta('exception-logs'),
    staleTime: 12 * HOURS,
    gcTime: 24 * HOURS,
    retry: 2,
  });

export const receiptsQueryOptions = (storeId: string) =>
  queryOptions<Receipt[]>({
    queryKey: ['stores', storeId, 'receipts'],
    meta: persistMeta('receipts'),
    staleTime: 12 * HOURS,
    gcTime: 24 * HOURS,
  });

export const receivingSessionsQueryOptions = (storeId: string) =>
  queryOptions<ReceivingSession[]>({
    queryKey: ['stores', storeId, 'receiving-sessions'],
    // WebSocket (receiving_session_updated) delivers real-time updates, so the
    // focus-refetch window can be generous.
    staleTime: 2 * MINUTES,
    gcTime: 1 * HOURS,
    retry: 2,
    // The WS subscription that keeps this list live only runs while the list
    // page is mounted. While the user is deep in a session the list is
    // unmounted and misses every update — including the terminal delete on
    // cancel/complete/abandon. So always re-validate on (re)entry, otherwise a
    // just-cancelled session lingers as a stale row until staleTime elapses.
    refetchOnMount: 'always',
  });

export const vendorInstructionsQueryOptions = (storeId: string, vendorNormalizedName: string) =>
  queryOptions<VendorInstructions>({
    queryKey: ['stores', storeId, 'receiving', 'instructions', vendorNormalizedName],
    staleTime: 24 * HOURS,
    gcTime: 24 * HOURS,
    retry: false,
  });

export const onboardingOverviewQueryOptions = () =>
  queryOptions<OnboardingOverview>({
    queryKey: ['admin', 'onboarding', 'overview'],
    staleTime: 5 * MINUTES,
    gcTime: 10 * MINUTES,
    retry: 2,
  });

export const onboardingEventsQueryOptions = (storeId: string) =>
  queryOptions<Event[]>({
    queryKey: ['admin', 'onboarding', 'events', storeId],
    staleTime: 5 * MINUTES,
    gcTime: 30 * MINUTES,
    retry: 2,
  });
